import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { LifeBuoy, X, Send, Sparkles, Loader2 } from "lucide-react";

const SUGGESTIONS = ["How do I build a flow?", "How do I connect WhatsApp?", "What is a Job?", "How do reminders work?"];

export default function HelpAssistant() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "bot", text: "Hi! 👋 I'm your DVEPL guide. Ask me how to use any feature and I'll walk you through it step by step." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, loading, open]);

  const send = async (textArg) => {
    const text = (typeof textArg === "string" ? textArg : input).trim();
    if (!text || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant", { message: text, page: location.pathname });
      setMsgs((m) => [...m, { role: "bot", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "bot", text: "Sorry, I couldn't answer just now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button data-testid="help-assistant-btn" onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-emerald-500 shadow-xl shadow-emerald-500/30 flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-6 h-6 text-white" /> : <LifeBuoy className="w-6 h-6 text-white" />}
      </button>

      {open && (
        <div data-testid="help-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] glass rounded-2xl flex flex-col overflow-hidden border border-emerald-500/25 shadow-2xl">
          <div className="flex items-center gap-2.5 px-4 h-14 border-b border-slate-700/50 bg-[#0d1420]">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-emerald-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            <div><p className="text-white text-sm font-semibold">DVEPL Guide</p><p className="text-[10px] text-emerald-400">Ask me anything about the app</p></div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[88%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "ml-auto rounded-tr-sm bg-emerald-600 text-white" : "rounded-tl-sm bg-slate-800 text-slate-200"}`}>{m.text}</div>
            ))}
            {loading && <div className="flex gap-1.5 pl-2"><span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" /><span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" style={{ animationDelay: "0.2s" }} /><span className="w-2 h-2 rounded-full bg-emerald-400 typing-dot" style={{ animationDelay: "0.4s" }} /></div>}
            {msgs.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid={`help-suggest`} onClick={() => send(s)} className="text-xs px-2.5 py-1.5 rounded-full border border-slate-600 text-slate-300 hover:border-emerald-500/50 transition-colors">{s}</button>
                ))}
              </div>
            )}
          </div>
          <div className="p-3 border-t border-slate-700/50 flex gap-2">
            <Input data-testid="help-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask how to use…" className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500 text-sm" />
            <button data-testid="help-send-btn" onClick={() => send()} disabled={loading} className="w-10 h-10 rounded-lg bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center shrink-0">
              {loading ? <Loader2 className="w-4 h-4 animate-spin text-[#0B0F17]" /> : <Send className="w-4 h-4 text-[#0B0F17]" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
